import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { reviewApplication } from 'submission/lib/partner-api'
import type { Application, ReviewBody, ReviewResponse, Tier } from './types'
import { TIERS } from './types'

type Props = {
  application: Application | null
  onClose: () => void
  onReviewed: (r: ReviewResponse) => void
  onError?: (e: Error) => void
}

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

const ReviewApplicationModal: React.FC<Props> = ({
  application,
  onClose,
  onReviewed,
  onError,
}) => {
  const { showToast } = useToast()
  const [tierOverride, setTierOverride] = useState<Tier | ''>('')
  const [payoutWallet, setPayoutWallet] = useState('')
  const [payoutChainId, setPayoutChainId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ReviewResponse | null>(null)

  useEffect(() => {
    setTierOverride('')
    setPayoutWallet('')
    setPayoutChainId('')
    setError(null)
    setResult(null)
  }, [application?.id])

  const submit = async (action: ReviewBody['action']) => {
    if (!application) return
    if (
      action === 'rejected' &&
      !window.confirm('Reject this application? This cannot be undone.')
    )
      return
    setSubmitting(true)
    setError(null)
    const body: ReviewBody = { action }
    if (action === 'approved') {
      if (tierOverride) body.tier_override = tierOverride
      if (payoutWallet.trim()) body.payout_wallet = payoutWallet.trim()
      if (payoutChainId.trim()) body.payout_chain_id = Number(payoutChainId)
    }
    try {
      const r = await reviewApplication(application.id, body)
      showToast({ type: 'success', message: `Application ${action}` })
      setResult(r)
      onReviewed(r)
    } catch (e) {
      const err = e as Error
      setError(err.message)
      showToast({ type: 'error', message: err.message })
      onError?.(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={application !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Review application{application ? ` · ${application.handle}` : ''}
          </DialogTitle>
        </DialogHeader>
        {application && (
          <div className="space-y-3 text-sm">
            <div className="text-neutral-600">
              X {application.x_handle || '—'} · Telegram{' '}
              {application.telegram_handle || '—'} · Audience{' '}
              {application.audience_size}
            </div>
            {application.note && (
              <p className="whitespace-pre-wrap">{application.note}</p>
            )}
            {result?.partner ? (
              <div className="rounded border border-green-200 bg-green-50 p-3">
                Partner created with code{' '}
                <span className="font-mono">{result.partner.code}</span>.{' '}
                <Link
                  to={`/partners/${result.partner.id}`}
                  className="text-brand-primary underline"
                >
                  Open partner
                </Link>
              </div>
            ) : (
              <>
                <label className="block">
                  Tier override
                  <select
                    className={inputCls}
                    value={tierOverride}
                    onChange={(e) =>
                      setTierOverride(e.target.value as Tier | '')
                    }
                  >
                    <option value="">None (computed)</option>
                    {TIERS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  Payout wallet (optional)
                  <input
                    className={inputCls}
                    value={payoutWallet}
                    onChange={(e) => setPayoutWallet(e.target.value)}
                    placeholder="0x…"
                  />
                </label>
                <label className="block">
                  Payout chain ID (optional)
                  <input
                    className={inputCls}
                    type="number"
                    value={payoutChainId}
                    onChange={(e) => setPayoutChainId(e.target.value)}
                  />
                </label>
              </>
            )}
            {error && <div className="text-red-600">{error}</div>}
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button
                variant="destructive"
                onClick={() => submit('rejected')}
                disabled={submitting}
              >
                Reject
              </Button>
              <Button onClick={() => submit('approved')} disabled={submitting}>
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ReviewApplicationModal
